// utils/filamentUtils.ts

export interface FilamentFeatures {
  spoolDesign?: string;
  layerAdhesion?: string;
  surfaceFinish?: string;
  weatherResistance?: string;
  consistentExtrusion?: string;
  enhancedStrength?: string;
  enhancedDurability?: string;
  highHeatResistance?: string;
  highFlexibility?: string;
  highSpeedCompatibility?: string;
  versatileApplications?: string;
  beginnerFriendly?: string;
  functionalApplications?: string;
  [key: string]: string | undefined;
}

export const parseFilamentFeatures = (features: any): FilamentFeatures => {
  if (!features) return {};
  
  if (typeof features === 'object' && !Array.isArray(features)) {
    return features as FilamentFeatures;
  }
  
  if (typeof features === 'string') {
    try {
      const parsed = JSON.parse(features);
      return parsed as FilamentFeatures;
    } catch (e) {
      console.warn('Failed to parse features string:', features);
      return {};
    }
  }
  
  if (Array.isArray(features) && features.length === 0) {
    return {};
  }
  
  return {};
};

/**
 * Prefer first-class includesReusableSpool; fall back to legacy features.spoolDesign.
 */
export const getSpoolDesignStatus = (
  featuresOrProduct: any,
  maybeFeatures?: any
): 'reusable' | 'not_reusable' | 'unknown' => {
  // Allow getSpoolDesignStatus(product) or getSpoolDesignStatus(features) or getSpoolDesignStatus(flag, features)
  let includesFlag: boolean | undefined;
  let features: any = featuresOrProduct;

  if (
    featuresOrProduct &&
    typeof featuresOrProduct === 'object' &&
    !Array.isArray(featuresOrProduct) &&
    ('includesReusableSpool' in featuresOrProduct || 'features' in featuresOrProduct || 'category' in featuresOrProduct)
  ) {
    if (typeof featuresOrProduct.includesReusableSpool === 'boolean') {
      includesFlag = featuresOrProduct.includesReusableSpool;
    }
    features = featuresOrProduct.features ?? maybeFeatures;
  } else if (typeof featuresOrProduct === 'boolean') {
    includesFlag = featuresOrProduct;
    features = maybeFeatures;
  }

  if (typeof includesFlag === 'boolean') {
    return includesFlag ? 'reusable' : 'not_reusable';
  }

  const parsed = parseFilamentFeatures(features);
  if (!parsed.spoolDesign) return 'unknown';
  if (parsed.spoolDesign === '-' || parsed.spoolDesign === '') return 'not_reusable';
  return 'reusable';
};

export const hasReusableSpool = (featuresOrProduct: any): boolean => {
  return getSpoolDesignStatus(featuresOrProduct) === 'reusable';
};

export const isFilamentCategory = (category: string | undefined): boolean => {
  return category === 'filament' || category?.toLowerCase() === 'filament';
};

// The spool product ID from your database
export const REUSABLE_SPOOL_PRODUCT_ID = 313;

// Fallback price (will be overwritten by database value)
export const REUSABLE_SPOOL_PRICE = 6.18;
